/**
 * The silent bars of one part, gathered into the runs a printed part shows as one bar.
 *
 * A wind player with thirty bars' rest is given one bar with `30` written over it, and
 * counts. Nothing about the music changes: the bars are still there, still silent, still
 * as long as their metre. This is a way of *printing* them, which is why nothing here is
 * stored on a bar -- the runs are worked out from what the part holds whenever they are
 * needed, so an edit that breaks a run into two cannot leave a stale count behind.
 *
 * A run is broken by anything the player has to read. A key change, a metre change, a
 * tempo, a rehearsal letter, a repeat sign, an ending, a segno, a double bar or a forced
 * break all end the run at that point, because a count drawn over them would hide them.
 * This is the whole of the convention: a multi-bar rest may contain nothing a player would
 * have looked at.
 */

import { capacityOf } from "./query";
import type { GlobalMeasure, Part, Score } from "./types";

/** A stretch of silent bars printed as one: `count` bars beginning at bar `start`. */
export interface RestRun {
  /** Index into `Score.measures` of the first bar of the run. */
  readonly start: number;
  /** How many bars the run holds, which is the number drawn over it. */
  readonly count: number;
}

/** The fewest bars worth gathering. Two is what every engraver uses. */
export const LEAST_RUN = 2;

/**
 * Whether a part has nothing but silence in a bar.
 *
 * Silence is stricter than "no notes". A bar carrying a dynamic, a chord symbol, a figure,
 * a clef change or an instrument to pick up has something the player reads, so it is not
 * empty even with no note in it, and gathering it away would lose the instruction.
 */
export function silentBar(score: Score, part: Part, index: number): boolean {
  const global = score.measures[index];
  if (global === undefined) return false;
  const measure = part.measures[global.id];
  if (measure === undefined) return false;
  if (measure.directions.length > 0) return false;
  if (measure.harmonies.length > 0 || measure.figures.length > 0) return false;
  if (Object.keys(measure.clefChanges).length > 0) return false;
  // A bar where the player picks up another instrument is a bar with the name of that
  // instrument printed over it, which is the last thing to gather away from a player.
  if (measure.instrument !== null) return false;
  return measure.voices.every((voice) => voice.events.every((event) => event.kind === "rest"));
}

/** What a bar states about the piece, rather than about any one player. */
function statesSomething(global: GlobalMeasure): boolean {
  return (
    global.time !== null ||
    global.key !== null ||
    global.tempo !== null ||
    global.rehearsal !== null ||
    global.breakBefore !== null
  );
}

/** A bar that a player has to see the start of: a repeat opens, an ending opens, a sign. */
function opensSomething(global: GlobalMeasure): boolean {
  const repeat = global.repeat;
  const jump = global.jump;
  return (
    repeat?.start === true ||
    (repeat?.endings.length ?? 0) > 0 ||
    jump?.segno === true ||
    jump?.coda === true
  );
}

/** A bar that a player has to see the end of: a repeat closes, a double bar, a jump. */
function closesSomething(global: GlobalMeasure): boolean {
  const repeat = global.repeat;
  const jump = global.jump;
  return (
    repeat?.end === true ||
    (repeat?.endings.length ?? 0) > 0 ||
    global.barline !== "regular" ||
    jump?.toCoda === true ||
    jump?.fine === true ||
    (jump !== null && jump.jump !== null)
  );
}

/** Whether a bar may stand inside a run at all, silence aside. */
function gatherable(score: Score, part: Part, index: number): boolean {
  const global = score.measures[index];
  if (global === undefined) return false;
  // A pickup, or the short bar that closes a repeat begun mid-bar. It is not a whole bar's
  // rest, so it cannot be counted as one.
  if (global.length !== null) return false;
  return silentBar(score, part, index);
}

/** Whether a bar has to begin a run rather than join the one before it. */
function begins(score: Score, index: number): boolean {
  const global = score.measures[index];
  if (global === undefined) return true;
  if (statesSomething(global) || opensSomething(global)) return true;
  const previous = score.measures[index - 1];
  return previous !== undefined && closesSomething(previous);
}

export interface RestRunOptions {
  /** The fewest bars gathered; a shorter stretch is left as the bars it is. */
  readonly least?: number;
}

/**
 * Every run of silent bars in a part, in order, longest allowed rather than fewest.
 *
 * Runs never overlap and never touch: two runs with nothing between them would have been
 * one, so a gap between them is a bar that is either sounding or one a player must read.
 */
export function restRuns(
  score: Score,
  part: Part,
  options: RestRunOptions = {},
): readonly RestRun[] {
  const least = Math.max(2, Math.floor(options.least ?? LEAST_RUN));
  const runs: RestRun[] = [];
  let start = -1;

  const close = (end: number): void => {
    if (start < 0) return;
    const count = end - start;
    if (count >= least) runs.push({ start, count });
    start = -1;
  };

  for (let index = 0; index < score.measures.length; index += 1) {
    if (!gatherable(score, part, index)) {
      close(index);
      continue;
    }
    if (begins(score, index)) close(index);
    if (start < 0) start = index;
    // A bar whose end has to be seen closes the run at the bar after it.
    const global = score.measures[index];
    if (global !== undefined && closesSomething(global)) close(index + 1);
  }
  close(score.measures.length);

  return runs;
}

/** The runs by the bar each begins at, which is how an exporter or a renderer asks. */
export function restRunsByStart(
  score: Score,
  part: Part,
  options: RestRunOptions = {},
): ReadonlyMap<number, number> {
  const byStart = new Map<number, number>();
  for (const run of restRuns(score, part, options)) byStart.set(run.start, run.count);
  return byStart;
}

/**
 * The bar a caret lands on when it steps onto `index`, given which way it is moving.
 *
 * A gathered run is drawn as one bar, so it is one bar to move through: stepping into the
 * middle of it would put the caret where nothing is drawn, with nothing on the page to
 * say where it went. Stepping forward over a run therefore comes out the far side, and
 * stepping back into one lands on its head, which is the bar the count is drawn over.
 *
 * A score that does not gather is unaffected, and so is a bar that is a run's head: that
 * bar is drawn, and typing a note into it breaks the run, which is how a rest is filled.
 */
export function barStep(score: Score, part: Part, index: number, direction: number): number {
  if (!score.layout.multiRests) return index;
  const run = restRunAt(restRuns(score, part), index);
  if (run === null || run.start === index) return index;
  return direction < 0 ? run.start : run.start + run.count;
}

/** The run a bar stands inside, or `null` when it is printed as itself. */
export function restRunAt(runs: readonly RestRun[], index: number): RestRun | null {
  return runs.find((run) => index >= run.start && index < run.start + run.count) ?? null;
}

/**
 * How long a run lasts, in ticks, so that playback and a timeline can measure it.
 *
 * The bars are unchanged and still sound one after another; this is only the sum of what
 * the run covers, for a caller drawing the run as one bar and needing its width in time.
 */
export function restRunTicks(score: Score, run: RestRun): number {
  let total = 0;
  for (let index = run.start; index < run.start + run.count; index += 1) {
    total += capacityOf(score, index);
  }
  return total;
}
